import React from 'react';
import {
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesButton,
  AgnesDateRangePickerInput,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';
import {
  STEP_LABEL,
  CLASSIFICATION_LABEL,
  RISK_LABEL,
  FILTROS_OPCOES_VAZIO,
  type CboTableFiltros,
  type CboFiltrosOpcoes,
} from '@/lib/cbo';
import { labelFiltro, protegerSelectDoFechamento } from '@/lib/filtros';

export type CboFilterOption = { value: string; label: string };

export type CboFilterField = {
  key: string;
  endKey?: string;
  placeholder: string;
  type?: 'select' | 'dateRange';
  options?: CboFilterOption[];
};

const TRIGGER_CLS =
  'w-full min-w-0 [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const optsFromLabels = (labels: Record<string, string>): CboFilterOption[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const strOpts = (valores: string[]): CboFilterOption[] =>
  valores.map((v) => ({ value: v, label: v }));

const SIM_NAO: CboFilterOption[] = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const CAMPOS = [
  { key: 'etapa', placeholder: 'Etapa da análise', opcoes: () => optsFromLabels(STEP_LABEL) },
  {
    key: 'classificacao',
    placeholder: 'Classificação de análise',
    opcoes: () => optsFromLabels(CLASSIFICATION_LABEL),
  },
  { key: 'risco', placeholder: 'Grau de risco', opcoes: () => optsFromLabels(RISK_LABEL) },
  { key: 'ocupacao', placeholder: 'Ocupação', opcoes: (o: CboFiltrosOpcoes) => o.ocupacao },
  {
    key: 'ocupacaoRecomendada',
    placeholder: 'Ocupação (Recomendação)',
    opcoes: (o: CboFiltrosOpcoes) => o.ocupacao,
  },
  {
    key: 'descricaoAtividades',
    placeholder: 'Descrição de atividade',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.descricaoAtividades),
  },
  {
    key: 'descricaoAtividadesRecomendada',
    placeholder: 'Descrição de atividade (Recomendação)',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.descricaoAtividadesRecomendada),
  },
  { key: 'cargo', placeholder: 'Cargo', opcoes: (o: CboFiltrosOpcoes) => strOpts(o.cargo) },
  { key: 'setor', placeholder: 'Setor', opcoes: (o: CboFiltrosOpcoes) => strOpts(o.setor) },
  {
    key: 'ambienteS2240',
    placeholder: 'Ambiente de trabalho S-2240',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.ambienteS2240),
  },
  {
    key: 'ambienteS2200S2206',
    placeholder: 'Ambiente de trabalho S-2200/S-2206',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.ambienteS2200S2206),
  },
  {
    key: 'regimeTrabalhista',
    placeholder: 'Regime trabalhista',
    opcoes: (o: CboFiltrosOpcoes) => strOpts(o.regimeTrabalhista),
  },
  {
    key: 'grauInstrucao',
    placeholder: 'Grau de instrução',
    opcoes: (o: CboFiltrosOpcoes) => o.grauInstrucao,
  },
  {
    key: 'escolaridadeMinima',
    placeholder: 'Escolaridade mínima',
    opcoes: (o: CboFiltrosOpcoes) => o.escolaridadeMinima,
  },
  { key: 'cotaAprendizagem', placeholder: 'Cota de aprendizagem', opcoes: () => SIM_NAO },
  {
    key: 'orgaoClasse',
    placeholder: 'Órgão de classe',
    opcoes: (o: CboFiltrosOpcoes) => o.orgaoClasse,
  },
  { key: 'pcd', placeholder: 'PCD', opcoes: () => SIM_NAO },
  { key: 'categoria', placeholder: 'Categoria', opcoes: (o: CboFiltrosOpcoes) => o.categoria },
] as const;

type FiltroForm = Record<(typeof CAMPOS)[number]['key'], string>;

function toFiltros(f: FiltroForm): CboTableFiltros {
  const one = (v: string) => (v ? [v] : []);
  const oneNum = (v: string) => (v ? [Number(v)] : []);
  const oneBool = (v: string) => (v === '' ? null : v === 'true');
  return {
    step: one(f.etapa),
    status: [],
    classification: one(f.classificacao),
    risk: one(f.risco),
    analysisOccupationCode: one(f.ocupacaoRecomendada),
    analysisOccurrenceId: [],
    occupationCode: one(f.ocupacao),
    activityDescription: one(f.descricaoAtividades),
    activityDescriptionRecommended: one(f.descricaoAtividadesRecomendada),
    cargo: one(f.cargo),
    setor: one(f.setor),
    nociveAgentEnvironment: one(f.ambienteS2240),
    legalAgreementEnvironment: one(f.ambienteS2200S2206),
    regimeTrabalhista: one(f.regimeTrabalhista),
    grauInstrucao: one(f.grauInstrucao),
    escolaridadeMinima: oneNum(f.escolaridadeMinima),
    cotaAprendizagem: oneBool(f.cotaAprendizagem),
    orgaoClasse: oneNum(f.orgaoClasse),
    pcd: oneBool(f.pcd),
    categoria: oneNum(f.categoria),
  };
}

type Props<T = CboTableFiltros> = {
  opcoes?: CboFiltrosOpcoes;
  onApply: (f: T) => void;
  count?: number;
  dataQaPrefix?: string;
  fields?: CboFilterField[];
  initialValues?: Record<string, string>;
  toFilter?: (form: Record<string, string>) => T;
};

type SelectFieldProps = {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: CboFilterOption[];
  dataQa: string;
};
function SelectField({ value, onValueChange, placeholder, options, dataQa }: SelectFieldProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <AgnesSelect value={value} onValueChange={onValueChange}>
      <AgnesSelectTrigger className={TRIGGER_CLS} dataQa={dataQa}>
        <AgnesSelectValue placeholder={placeholder} title={selectedLabel} />
      </AgnesSelectTrigger>
      <AgnesSelectContent
        position="popper"
        dataQa={`${dataQa}-lista`}
        className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]"
      >
        {options.map((o) => (
          <AgnesSelectItem
            key={o.value}
            value={o.value}
            dataQa={dataQa}
            className="[&>span:last-child]:min-w-0"
          >
            <span className="block w-full min-w-0 truncate" title={o.label}>
              {o.label}
            </span>
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );
}

export default function CboFilter<T = CboTableFiltros>({
  opcoes,
  onApply,
  count = 0,
  dataQaPrefix = 'cbo-filtro',
  fields,
  initialValues,
  toFilter,
}: Props<T>) {
  const [open, setOpen] = React.useState(false);
  const opcoesCbo = opcoes ?? FILTROS_OPCOES_VAZIO;
  const campos: CboFilterField[] =
    fields ??
    CAMPOS.map((campo) => ({
      key: campo.key,
      placeholder: campo.placeholder,
      options: campo.opcoes(opcoesCbo),
    }));
  const makeEmpty = (): Record<string, string> =>
    initialValues
      ? { ...initialValues }
      : Object.fromEntries(
          campos.flatMap((campo) =>
            [campo.key, ...(campo.endKey ? [campo.endKey] : [])].map((key) => [key, '']),
          ),
        );
  const [f, setF] = React.useState<Record<string, string>>(makeEmpty);
  const set = (key: string) => (value: string) => setF((current) => ({ ...current, [key]: value }));
  const toAppliedFilters = (form: Record<string, string>) =>
    toFilter ? toFilter(form) : (toFiltros(form as FiltroForm) as T);
  const qa = (suffix: string) => `${dataQaPrefix}-${suffix}`;

  const aplicar = () => {
    onApply(toAppliedFilters(f));
    setOpen(false);
  };
  const limpar = () => {
    const vazio = makeEmpty();
    setF(vazio);
    onApply(toAppliedFilters(vazio));
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" data-qa={qa('icone-filtro')} />}
          data-qa={qa('trigger')}
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-24px)] p-0"
        dataQa={qa('popover')}
        onInteractOutside={protegerSelectDoFechamento}
      >
        <div
          data-qa={qa('cabecalho')}
          className="flex items-center justify-between border-b border-agnes-border px-4 py-3"
        >
          <span data-qa={qa('titulo')} className="text-base font-semibold text-agnes-text">
            Filtro
          </span>
          <button
            type="button"
            data-qa={qa('fechar')}
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" data-qa={qa('fechar-icone')} />
          </button>
        </div>

        <div
          data-qa={qa('campos')}
          className="flex min-w-0 max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4 [&>*]:shrink-0"
        >
          {campos.map((campo) => {
            const campoQa = qa(`campo-${campo.key}`);
            if (campo.type === 'dateRange' && campo.endKey) {
              return (
                <div
                  key={campo.key}
                  data-qa={`${campoQa}-intervalo`}
                  className="flex flex-col gap-1"
                >
                  <AgnesDateRangePickerInput
                    value={
                      f[campo.key] || f[campo.endKey]
                        ? [f[campo.key] ?? '', f[campo.endKey] ?? '']
                        : []
                    }
                    onChange={(dates) =>
                      setF((current) => ({
                        ...current,
                        [campo.key]: dates[0] ?? '',
                        [campo.endKey!]: dates[1] ?? '',
                      }))
                    }
                    label={campo.placeholder}
                    dataQa={campoQa}
                  />
                </div>
              );
            }
            return (
              <SelectField
                key={campo.key}
                value={f[campo.key] ?? ''}
                onValueChange={set(campo.key)}
                dataQa={campoQa}
                placeholder={campo.placeholder}
                options={campo.options ?? []}
              />
            );
          })}
        </div>

        <div
          data-qa={qa('acoes')}
          className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3"
        >
          <AgnesButton
            variant="primary"
            onClick={aplicar}
            className="w-full"
            data-qa={qa('aplicar')}
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            data-qa={qa('limpar')}
            startIcon={<Trash2 className="h-4 w-4" data-qa={qa('limpar-icone')} />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
